import type {TaillightsStylingDirective, TaillightsSyntaxColors} from './taillights-worker'

// Foregrounds from syntax-theme-prettylights.scss. Background-dependent light
// foregrounds intentionally use the ordinary text color until fills are supported.
export const taillightsSyntaxColorVariables: Record<string, string> = {
  'pl-c': '--color-prettylights-syntax-comment',
  'pl-c1': '--color-prettylights-syntax-constant',
  'pl-s pl-v': '--color-prettylights-syntax-constant',
  'pl-e': '--color-prettylights-syntax-entity',
  'pl-en': '--color-prettylights-syntax-entity',
  'pl-smi': '--color-prettylights-syntax-storage-modifier-import',
  'pl-s pl-s1': '--color-prettylights-syntax-storage-modifier-import',
  'pl-ent': '--color-prettylights-syntax-entity-tag',
  'pl-k': '--color-prettylights-syntax-keyword',
  'pl-s': '--color-prettylights-syntax-string',
  'pl-pds': '--color-prettylights-syntax-string',
  'pl-s pl-pse pl-s1': '--color-prettylights-syntax-string',
  'pl-sr': '--color-prettylights-syntax-string',
  'pl-sr pl-sre': '--color-prettylights-syntax-string',
  'pl-sr pl-sra': '--color-prettylights-syntax-string',
  'pl-v': '--color-prettylights-syntax-variable',
  'pl-smw': '--color-prettylights-syntax-variable',
  'pl-bu': '--color-prettylights-syntax-brackethighlighter-unmatched',
  'pl-ii': '--fgColor-default',
  'pl-c2': '--fgColor-default',
  'pl-sr pl-cce': '--color-prettylights-syntax-string-regexp',
  'pl-ml': '--color-prettylights-syntax-markup-list',
  'pl-mh': '--color-prettylights-syntax-markup-heading',
  'pl-mh pl-en': '--color-prettylights-syntax-markup-heading',
  'pl-ms': '--color-prettylights-syntax-markup-heading',
  'pl-mi': '--color-prettylights-syntax-markup-italic',
  'pl-mb': '--color-prettylights-syntax-markup-bold',
  'pl-md': '--color-prettylights-syntax-markup-deleted-text',
  'pl-mi1': '--color-prettylights-syntax-markup-inserted-text',
  'pl-mc': '--color-prettylights-syntax-markup-changed-text',
  'pl-mi2': '--fgColor-default',
  'pl-mdr': '--color-prettylights-syntax-meta-diff-range',
  'pl-ba': '--color-prettylights-syntax-brackethighlighter-angle',
  'pl-sg': '--color-prettylights-syntax-sublimelinter-gutter-mark',
  'pl-corl': '--color-prettylights-syntax-constant-other-reference-link',
}

export type TaillightsGlyphStyle = {bold: boolean; italic: boolean; underline: boolean}
export type TaillightsStyleRun = TaillightsGlyphStyle & {start: number; end: number; colorKey?: string}

const rules = Object.keys(taillightsSyntaxColorVariables).map(key => ({key, selector: key.split(' ')}))
const boldClasses = new Set(['pl-mh', 'pl-ms', 'pl-mb', 'pl-mdr'])

function matches(selector: string[], ancestry: string[][]) {
  if (!ancestry.at(-1)?.includes(selector.at(-1) ?? '')) return false
  let ancestor = ancestry.length - 2
  for (let part = selector.length - 2; part >= 0; part--) {
    while (ancestor >= 0 && !ancestry[ancestor]?.includes(selector[part] ?? '')) ancestor--
    if (ancestor < 0) return false
    ancestor--
  }
  return true
}

export function sameTaillightsGlyphStyle(left: TaillightsGlyphStyle, right: TaillightsGlyphStyle) {
  return left.bold === right.bold && left.italic === right.italic && left.underline === right.underline
}

function* taillightsStyleRuns(
  length: number,
  directives: readonly TaillightsStylingDirective[] = [],
): Generator<TaillightsStyleRun> {
  if (length === 0) return
  type Range = {start: number; end: number; directive: TaillightsStylingDirective; classes?: string[]}
  const ranges = directives
    .map((directive): Range => {
      const [start, end] = Array.isArray(directive) ? directive : [directive.s, directive.e]
      return {start: Math.max(0, start), end: Math.min(length, end), directive}
    })
    .filter(range => Number.isInteger(range.start) && Number.isInteger(range.end) && range.end > range.start)
    .sort((left, right) => left.start - right.start || right.end - left.end)
  const events = ranges
    .flatMap(range => [
      {offset: range.start, range, entering: true},
      {offset: range.end, range, entering: false},
    ])
    .sort((left, right) => left.offset - right.offset)
  // Insertion order matches the canonical ancestry order, including equal and overlapping ranges.
  const active = new Set<Range>()
  let eventIndex = 0
  for (let start = 0; start < length; ) {
    while (events[eventIndex]?.offset === start) {
      const event = events[eventIndex++]
      if (!event) break
      if (event.entering) active.add(event.range)
      else active.delete(event.range)
    }
    const end = events[eventIndex]?.offset ?? length
    const run: TaillightsStyleRun = {start, end, bold: false, italic: false, underline: false}
    const ancestry: string[][] = []
    for (const range of active) {
      const classes = (range.classes ??=
        (Array.isArray(range.directive) ? range.directive[2] : range.directive.c)?.split(/\s+/).filter(Boolean) ?? [])
      ancestry.push(classes)
      // Child declarations override inherited color; specificity only compares
      // selectors matching the same node, not its parent.
      let specificity = 0
      let colorKey: string | undefined
      for (const rule of rules) {
        if (rule.selector.length >= specificity && matches(rule.selector, ancestry)) {
          specificity = rule.selector.length
          colorKey = rule.key
        }
      }
      if (colorKey) run.colorKey = colorKey
      else if (classes.length) {
        const customClass = classes.find(cssClass => !cssClass.startsWith('pl-'))
        if (customClass) run.colorKey = customClass
      }
      run.bold ||= classes.some(cssClass => boldClasses.has(cssClass)) || matches(['pl-sr', 'pl-cce'], ancestry)
      run.italic ||= classes.includes('pl-mi')
      run.underline ||= classes.includes('pl-corl')
    }
    yield run
    start = end
  }
}

/** Resolve range containment as ancestry; equal ranges retain directive order. */
export function resolveTaillightsStyleRuns(
  length: number,
  directives: readonly TaillightsStylingDirective[] = [],
): TaillightsStyleRun[] {
  const runs: TaillightsStyleRun[] = []
  for (const run of taillightsStyleRuns(length, directives)) {
    const previous = runs.at(-1)
    if (previous && previous.colorKey === run.colorKey && sameTaillightsGlyphStyle(previous, run))
      previous.end = run.end
    else runs.push(run)
  }
  return runs
}

export function getTaillightsGlyphRuns(runs: readonly TaillightsStyleRun[]): TaillightsStyleRun[] {
  const result: TaillightsStyleRun[] = []
  for (const run of runs) {
    const previous = result.at(-1)
    if (previous && sameTaillightsGlyphStyle(previous, run)) previous.end = run.end
    else result.push({...run, colorKey: undefined})
  }
  return result
}

export function hasTaillightsSyntaxHighlighting(
  lines: readonly string[],
  directives: readonly TaillightsStylingDirective[][],
) {
  return directives.some((row, index) => {
    if (!row.length) return false
    for (const run of taillightsStyleRuns(lines[index]?.length ?? 0, row)) {
      if (
        run.bold ||
        run.italic ||
        run.underline ||
        (run.colorKey &&
          Object.hasOwn(taillightsSyntaxColorVariables, run.colorKey) &&
          taillightsSyntaxColorVariables[run.colorKey] !== '--fgColor-default')
      )
        return true
    }
    return false
  })
}

export function hasTaillightsColors(
  runs: readonly TaillightsStyleRun[],
  {colors, defaultColor}: {colors: TaillightsSyntaxColors; defaultColor: string},
) {
  return runs.some(
    run =>
      run.end > run.start &&
      run.colorKey &&
      Object.hasOwn(colors, run.colorKey) &&
      colors[run.colorKey] &&
      colors[run.colorKey] !== defaultColor,
  )
}
